import React, { useCallback, useRef, useState, useEffect } from "react";
import { icons, colors2, UI_CAPTION_SEMIBOLD_DF, UI_CAPTION_DF, ButtonSecondaryCompact, TagDisplay, TagDisplayV2 } from "oolib";
import { getAnnoFilterBucketKey } from "@okf/ootils";
import {
  StyledBlockWrapper,
  StyledBlockHeader,
  StyledBlockHeaderFirstRow,
  StyledBlockHeaderRight,
  StyledBlockHeaderFullRow,
  StyledBlockSubHeader,
  StyledIndirectFilterHeader,
} from "./styled";
import { FilterValues } from "./FilterValues";
import { IntraFilterOperatorSelect } from "./IntraFilterOperatorSelect";
import TooltipV2 from "@/components/UI/Tooltip";
import { FilterOptionsPanel } from "@/components/inputs/DynamicTagsInputs/FilterInput/comps/DocHierarchyFilter/comps/FilterOptionsPanel";
import { RANGE_FILTER_TYPES } from "@/components/inputs/DynamicTagsInputs/FilterInput/utils/filterConstants";
import { Tag } from "oolib/dist/v2/components/Tags";
import { colors } from "oolib/dist/v2/themes";

const { CaretRight, X: XIcon, ArrowElbowDownLeft } = icons;

// Rollup ("indirect") filters carry one of these filterTypes — used to surface
// an "Indirect Filter" header on the applied-filter block.
const ROLLUP_FILTER_TYPES = new Set(["nestedRollupTagType", "rollupValuePathType"]);

const formatDate = (d) => {
  if (!d) return "—";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date)) return String(d);
  return date.toLocaleDateString();
};

const DateRangeDisplay = ({ values }) => {
  if (!Array.isArray(values) || values.length < 2) return null;
  return (
    <UI_CAPTION_DF style={{ color: colors2.grey60, marginTop: "0.4rem" }}>
      {formatDate(values[0])} — {formatDate(values[1])}
    </UI_CAPTION_DF>
  );
};

/**
 * Unified applied filter block used by both Anno and Doc applied filters.
 *
 * @param {object}   block                 — block descriptor (key, filterId, level, levelIcon, levelLabel, breadcrumb, values, ...)
 * @param {array}    activeFilters         — full activeFilters array
 * @param {function} onChange              — (filterId, newValues) callback
 * @param {function} renderLightbox       — ({ block, activeFilters, onChange, isOpen }) => ReactNode
 * @param {boolean}  showIndependentLabel  — when true, prefixes "Independent" for breadcrumb-less non-top-level items (anno only)
 */
export const AppliedFilterBlock = ({
  block,
  activeFilters,
  onChange,
  renderLightbox,
  showIndependentLabel = false,
  intraFilterOperator = "OR",
  onIntraFilterOperatorChange,
  onRemoveSavedConfig,
  contentTypes,
  noAppliedValueChipsTruncation = false,
  showDatasetGuidance = false,
  onDismissDatasetGuidance,
  showIndirectGuidance = false,
  onDismissIndirectGuidance,
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const breadcrumbRef = useRef(null);

  // First-use guidance popover for multi-dataset ("N Datasets") filters.
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  useEffect(() => {
    if (showDatasetGuidance) setGuidanceOpen(true);
  }, [showDatasetGuidance]);

  useEffect(() => {
    const el = breadcrumbRef.current;
    if (!el) return;
    const check = () => setIsOverflowing(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [block.breadcrumb, block.levelLabel]);

  const handleClearBlock = useCallback((e) => {
    e.stopPropagation();
    if (block.annoFilterBucketKey != null) {
      const currentFilter = activeFilters.find((f) => f.filterId === block.filterId);
      const currentValues = currentFilter?.values || [];
      const newValues = currentValues.filter(
        (v) => getAnnoFilterBucketKey(block.level, v) !== block.annoFilterBucketKey
      );
      onChange(block.filterId, newValues);
      return;
    }
    onChange(block.filterId, []);
  }, [onChange, activeFilters, block.filterId, block.annoFilterBucketKey, block.level]);

  const handleRemoveOption = useCallback(
    (valueToRemove) => {
      const currentFilter = activeFilters.find(
        (f) => f.filterId === block.filterId
      );
      const currentValues = currentFilter?.values || [];
      const newValues = currentValues.filter(
        (v) => v.value !== valueToRemove.value
      );
      onChange(block.filterId, newValues);
    },
    [activeFilters, block.filterId, onChange]
  );

  const IconComp = icons[block.levelIcon];
  const hasBreadcrumb = block.breadcrumb.length > 0;
  const isRollupFilter =
    ROLLUP_FILTER_TYPES.has(block.filterConfig?.target?.filterType) ||
    ROLLUP_FILTER_TYPES.has(block.filterConfig?.source?.filterType);
  // Auto-generated chain description, e.g. "Indirect filter: City > Locality > Respondent Profile 2".
  const indirectTooltip = block.filterConfig?.tooltip;
  const filterType = block.filterConfig?.source?.filterType;
  const isRangeType = RANGE_FILTER_TYPES.has(filterType);
  const isDateRange = filterType === "dateRangeType";
  const isNumberRange = filterType === "numberRangeType";
  const hasValues = Array.isArray(block.values)
    ? block.values.length > 0
    : block.values != null && typeof block.values === "object" && Object.keys(block.values).length > 0;

  // "Indirect Filter" tooltip is two-phase, mirroring the dataset guidance:
  //  Phase 1 (guidance): when the parent flags this block (first indirect filter,
  //    gated once-per-session), auto-open a coachmark popover WITH an Okay button.
  //  Phase 2 (after acknowledged, or for non-first blocks): a plain hover tooltip
  //    of the chain (no Okay); hover shows it, click pins it (native TooltipV2).
  const [indirectOpen, setIndirectOpen] = useState(false);
  const [indirectAcknowledged, setIndirectAcknowledged] = useState(false);
  useEffect(() => {
    if (showIndirectGuidance) setIndirectOpen(true);
  }, [showIndirectGuidance]);
  const acknowledgeIndirect = useCallback(() => {
    setIndirectOpen(false);
    setIndirectAcknowledged(true);
    onDismissIndirectGuidance?.();
  }, [onDismissIndirectGuidance]);
  const showIndirectCoachmark = showIndirectGuidance && !indirectAcknowledged;

  const isSavedConfig = !hasValues;

  const displayLabel =
    showIndependentLabel && !hasBreadcrumb && block.level !== "themes" && block.level !== "commonAnnoFilter"
      ? `Independent ${block.levelLabel}`
      : block.levelLabel;

  // For saved config blocks: treat "Independent" items as having a virtual breadcrumb
  const isIndependentItem = showIndependentLabel && !hasBreadcrumb && block.level !== "themes" && block.level !== "commonAnnoFilter";


  const headerContent = (
    <>
      {isRollupFilter && (() => {
        const indirectHeader = (
          <StyledIndirectFilterHeader>
            {ArrowElbowDownLeft && (
              <ArrowElbowDownLeft
                size={14}
                color={colors2.purple}
                weight="bold"
                style={{ transform: "scaleX(-1)", flexShrink: 0 }}
              />
            )}
            <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.purple }}>
              Indirect Filter
            </UI_CAPTION_SEMIBOLD_DF>
          </StyledIndirectFilterHeader>
        );
        // Phase 2 (plain chain text) once acknowledged or for non-first blocks;
        // Phase 1 (auto-open coachmark with Okay) for the flagged first block.
        if (!indirectTooltip) return indirectHeader;
        // ootils builds the full indirect chain (field → connected datasets →
        // in-view dataset) and exposes it structured as `tooltipChain`, with the
        // in-view dataset as the last segment. Render it with `>` separators and
        // bold the last segment. Fall back to the joined tooltip string for any
        // config built by an older ootils that predates tooltipChain.
        const tooltipChain = block.filterConfig?.tooltipChain;
        const indirectContent =
          Array.isArray(tooltipChain) && tooltipChain.length ? (
            <UI_CAPTION_DF style={{ color: colors.white }}>
              {tooltipChain.map((seg, i) => (
                <React.Fragment key={i}>
                  {i > 0 && " > "}
                  {i === tooltipChain.length - 1 ? (
                    <span style={{ fontWeight: 700 }}>{seg}</span>
                  ) : (
                    seg
                  )}
                </React.Fragment>
              ))}
            </UI_CAPTION_DF>
          ) : (
            <UI_CAPTION_DF style={{ color: colors.white }}>
              {indirectTooltip.replace(/^Indirect filter:\s*/i, "")}
            </UI_CAPTION_DF>
          );
        return showIndirectCoachmark ? (
          <TooltipV2
            trigger="click"
            placement="top-start"
            wrapperDisplay="inline-block"
            open={indirectOpen}
            onOpenChange={(o) => {
              setIndirectOpen(o);
              if (!o) acknowledgeIndirect();
            }}
            primaryContent={{
              layout: "custom",
              render: (
                <div style={{ maxWidth: 280, display: "flex", flexDirection: "column", gap: 8, padding: "1rem" }}>
                  <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.white }}>
                    Indirect Filter
                  </UI_CAPTION_SEMIBOLD_DF>
                  {indirectContent}
                  <ButtonSecondaryCompact
                    onClick={acknowledgeIndirect}
                    style={{ background: "white", width: "5rem" }}
                  >
                    Okay
                  </ButtonSecondaryCompact>
                </div>
              ),
            }}
          >
            {indirectHeader}
          </TooltipV2>
        ) : (
          <TooltipV2
            placement="top-start"
            wrapperDisplay="inline-block"
            primaryContent={{
              layout: "custom",
              render: (
                <div style={{ padding: "0.8rem", maxWidth: "30rem" }}>
                  {indirectContent}
                </div>
              ),
            }}
          >
            {indirectHeader}
          </TooltipV2>
        );
      })()}
      <StyledBlockHeader>
        <StyledBlockHeaderFirstRow>
          {!hasBreadcrumb && IconComp && (
            <IconComp
              size={14}
              color={colors2.grey80}
              weight="bold"
              style={{ flexShrink: 0 }}
            />
          )}
          {(() => {
            const breadcrumbInner = (
              <div
                ref={breadcrumbRef}
                style={{
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  ...(isOverflowing ? { maskImage: "linear-gradient(to right, black 90%, transparent 100%)", WebkitMaskImage: "linear-gradient(to right, black 90%, transparent 100%)" } : {}),
                }}
              >
                <UI_CAPTION_SEMIBOLD_DF
                  style={{ color: hasBreadcrumb ? colors2.grey50 : colors2.grey80 }}
                >
                  {hasBreadcrumb ? block.breadcrumb[0] : displayLabel}
                </UI_CAPTION_SEMIBOLD_DF>
              </div>
            );

            // First-use guidance: an explanatory popover (with an Okay button)
            // shown once per session for the first multi-dataset filter.
            if (showDatasetGuidance) {
              const names =
                block.datasetNames ||
                (block.breadcrumbTooltip ? block.breadcrumbTooltip.split("\n") : []);
              const dismiss = () => {
                setGuidanceOpen(false);
                onDismissDatasetGuidance?.();
              };
              return (
                <TooltipV2
                  trigger="click"
                  placement="right"
                  open={guidanceOpen}
                  onOpenChange={(o) => {
                    setGuidanceOpen(o);
                    if (!o) onDismissDatasetGuidance?.();
                  }}
                  primaryContent={{
                    layout: "custom",
                    render: (
                      <div style={{ maxWidth: 280, display: "flex", flexDirection: "column", gap: 8, padding: "1rem" }}>
                        <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.white }}>
                          Applied across {names.length} datasets
                        </UI_CAPTION_SEMIBOLD_DF>
                        <UI_CAPTION_DF style={{ color: colors.white }}>
                          “{block.levelLabel}” is shared by these datasets, so this one filter narrows all of them at once:
                        </UI_CAPTION_DF>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {names.map((n) => (
                            <TagDisplayV2
                              size="XS"
                              key={n}
                              display={n}
                              colorPreset="inProgress"
                            />
                          ))}
                        </div>
                        <ButtonSecondaryCompact
                          onClick={dismiss}
                          style={{ background: "white", width: "5rem" }}
                        >
                          Okay
                        </ButtonSecondaryCompact>
                      </div>
                    ),
                  }}
                >
                  {breadcrumbInner}
                </TooltipV2>
              );
            }

            if (block.breadcrumbTooltip) {
              return (
                <TooltipV2 text={block.breadcrumbTooltip} style={{ whiteSpace: "pre-line" }}>
                  {breadcrumbInner}
                </TooltipV2>
              );
            }

            return breadcrumbInner;
          })()}
        </StyledBlockHeaderFirstRow>

        {hasBreadcrumb && block.breadcrumb.slice(1).map((seg, i) => (
          <StyledBlockHeaderFullRow key={i}>
            <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey50 }}>
              {`/ ${seg}`}
            </UI_CAPTION_SEMIBOLD_DF>
          </StyledBlockHeaderFullRow>
        ))}

        {!isNumberRange && (
          <StyledBlockHeaderRight {...(isSavedConfig ? { "data-saved-caret": true } : {})}>
            {!isRangeType && hasValues && onIntraFilterOperatorChange && (
              <IntraFilterOperatorSelect
                filterId={block.filterId}
                annoFilterBucketKey={block.annoFilterBucketKey}
                intraFilterOperator={intraFilterOperator}
                onIntraFilterOperatorChange={onIntraFilterOperatorChange}
              />
            )}
            {isSavedConfig ? (
              <div data-block-actions style={{ display: "flex", alignItems: "center", gap: "0.2rem" }}>
                {onRemoveSavedConfig && (
                  <div
                    role="button"
                    style={{ cursor: "pointer", display: "flex", alignItems: "center", padding: "0.2rem" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveSavedConfig(block);
                    }}
                  >
                    <XIcon size={16} color={colors2.grey50} weight="bold" />
                  </div>
                )}
                <CaretRight size={16} color={colors2.grey50} weight="bold" />
              </div>
            ) : (
              <div data-block-actions style={{ display: "flex", alignItems: "center", gap: "0.2rem" }}>
                <div
                  role="button"
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", padding: "0.2rem" }}
                  onClick={handleClearBlock}
                >
                  <XIcon size={16} color={colors2.grey80} weight="bold" />
                </div>
                <CaretRight size={16} color={colors2.grey80} weight="bold" />
              </div>
            )}
          </StyledBlockHeaderRight>
        )}
      </StyledBlockHeader>

      {hasBreadcrumb && (
        <StyledBlockSubHeader>
          {IconComp && (
            <IconComp
              size={14}
              color={colors2.grey80}
              weight="bold"
              style={{ flexShrink: 0 }}
            />
          )}
          <UI_CAPTION_SEMIBOLD_DF
            style={{ color: colors2.grey80, flex: 1 }}
          >
            {block.levelLabel}
          </UI_CAPTION_SEMIBOLD_DF>
        </StyledBlockSubHeader>
      )}
    </>
  );

  // The number-range histogram/bounds aggregation must query the collection the
  // field actually lives in. Prefer the filter's own contentType (resolved onto
  // filterConfig by groupDocFilters / findFilterConfig), falling back to the
  // page-level contentTypes. Passing the page-level set for a per-dataset filter
  // hits the wrong collection → backend errors → the popout loads forever.
  const blockContentTypes = block.filterConfig?.contentTypes?.length
    ? block.filterConfig.contentTypes
    : contentTypes;

  const blockBody = isNumberRange ? (
    <FilterOptionsPanel
      compact
      filterConfig={block.filterConfig}
      value={block.values}
      onChange={(newValues) => onChange(block.filterId, newValues)}
      contentTypes={blockContentTypes}
    />
  ) : isDateRange ? (
    <DateRangeDisplay values={block.values} />
  ) : hasValues ? (
    <FilterValues values={block.values} onRemove={handleRemoveOption} noAppliedValueChipsTruncation={noAppliedValueChipsTruncation} />
  ) : null;

  if (isNumberRange) {
    return (
      <StyledBlockWrapper $isSavedConfig={isSavedConfig} $isOpen={isLightboxOpen}>
        {headerContent}
        {blockBody}
      </StyledBlockWrapper>
    );
  }

  return renderLightbox({
    block,
    activeFilters,
    onChange,
    onOpenChange: setIsLightboxOpen,
    children: (
      <StyledBlockWrapper $isSavedConfig={isSavedConfig} $isOpen={isLightboxOpen} style={{ cursor: "pointer" }}>
        {headerContent}
        {blockBody}
      </StyledBlockWrapper>
    ),
  });
};
